import React from 'react'
import { Typography, Chip } from '@the-platform-group/component-library'
import { DataGrid, DataGridActionCell } from '@the-platform-group/component-library/DataGrid'
import { DATETIME } from '@the-platform-group/component-library/DataGrid/constant'
import { Delete as DeleteIcon } from '@the-platform-group/component-library/Icons'
import { convertTimezoneAndFormat } from '@the-platform-group/formatters/dateFormat'
import { FDS_STATUS_COLORS } from 'constants/fdsAdmin'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import useDeleteUserFdsadminDetail from '../hooks/useDeleteUserFdsadminDetail'

const BASE_COLUMNS = [
  {
    field: 'usernameSerial',
    headerName: 'Factset Username Serial Number',
    width: 200,
  },
  {
    field: 'status',
    width: 120,
    renderCell: ({ value }) => (
      <Chip
        label={value}
        size="small"
        color={FDS_STATUS_COLORS[value] || FDS_STATUS_COLORS.default}
      />
    ),
  },
  {
    field: 'updatedAt',
    headerName: 'Last Synced At',
    width: 200,
    type: DATETIME,
    valueGetter: ({ value }) => (value ? new Date(value) : null),
    valueFormatter: ({ value }) => convertTimezoneAndFormat(value),
  },
]

/**
 * @param {Array} props.details - array of FDS admin detail records for this user
 * @param {string} props.userId - the user's ID, used to invalidate the cache after deletion
 * @param {boolean} props.canDelete - whether to show the delete action (admin only)
 */
const UserFdsadminDetails = ({ details, userId, canDelete }) => {
  const { createConfirmationDialog } = useConfirmationDialog()
  const { deleteDetail, isDeleting } = useDeleteUserFdsadminDetail(userId)

  // `id` is the DataGrid row key (must be unique); `detailId` is the backend record id used for deletion
  const rows = (details || []).map((d, i) => ({ ...d, id: d.usernameSerial || i, detailId: d.id }))

  const columns = canDelete
    ? [
        ...BASE_COLUMNS,
        {
          field: 'actions',
          headerName: '',
          width: 60,
          sortable: false,
          renderCell: ({ row }) =>
            row.detailId ? (
              <DataGridActionCell
                icon={<DeleteIcon />}
                title="Delete serial number"
                disabled={isDeleting}
                onClick={() =>
                  createConfirmationDialog({
                    title: `Delete serial number ${row.usernameSerial}?`,
                    onConfirm: () => deleteDetail(row.detailId),
                  })
                }
              />
            ) : null,
        },
      ]
    : BASE_COLUMNS

  return (
    <>
      <Typography variant="h5" component="h2">
        FDS Admin Details
      </Typography>
      <DataGrid
        name="UserFdsadminDetailsTable"
        columns={columns}
        commonColumnProps={{ headerAlign: 'left' }}
        rows={rows}
        rowCount={rows.length}
        localeText={{
          noRowsLabel: 'No FDS admin details found for this user.',
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
        autoHeight
      />
    </>
  )
}

export default UserFdsadminDetails
